import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { finalize, map, Observable, of, shareReplay, Subject } from 'rxjs';
import { PERMISSIONS, PERMISSION_ACCESS } from 'src/app/shared/modules/permissions/enums/permissions.enum';
import { environment } from 'src/environments/environment';
import { EXEC_TYPE } from '../../../models/enums';
import { IJwtTokenUser, ResponseJson } from '../../../models/models';
import { AuthenticationService } from '../../../services/authentication.service';
import { BaseService } from '../../../services/base.service';
import { UserPermissions } from '../models/permissions.model';

@Injectable({ providedIn: 'root' })
export class PermissionsService extends BaseService {
    apiUrl = environment.apiUrl + '/permissions'

    currentUser!: IJwtTokenUser | null

    refreshPermissions: Subject<void> = new Subject<void>();
    currentPermissions=signal< UserPermissions[]| null>(null) 
    /** Tracks which user the in-memory permission cache belongs to. */
    private permissionCacheUserKey: string | null = null
    /** Shared in-flight fetch while the cache is cold; dedupes concurrent callers. */
    private permissionsLoad$: Observable<UserPermissions[]> | null = null
    /** User identity the in-flight fetch was started for. */
    private permissionsLoadUserKey: string | null = null
    accessLevelValueMap = {
        [PERMISSION_ACCESS.ADMIN]: 3,
        [PERMISSION_ACCESS.WRITE]: 2,
        [PERMISSION_ACCESS.READ]: 1,
    }

    constructor(
        protected override http: HttpClient,
        private authService: AuthenticationService
    ) {
        super(http)

        this.authService.currentUser.subscribe((user) => {
            const key = this.getUserCacheKey(user);
            const userChanged = key !== this.permissionCacheUserKey;

            // Always keep currentUser fresh so field-level updates on the same
            // user (e.g. role / isDev from a new token) are never dropped.
            this.currentUser = user;
            if (userChanged) {
                this.permissionCacheUserKey = key;
                this.invalidatePermissionCache();
                this.refreshPermissions.next();
            }
        });
    }

    /** Stable identity for scoping the permission cache to the logged-in user. */
    private getUserCacheKey(user: IJwtTokenUser | null): string | null {
        if (!user) {
            return null;
        }
        return user._id ?? user.contactNumber ?? null;
    }

    private invalidatePermissionCache(): void {
        this.currentPermissions.set(null);
        this.clearInFlightPermissionsLoad();
    }

    private clearInFlightPermissionsLoad(): void {
        this.permissionsLoad$ = null;
        this.permissionsLoadUserKey = null;
    }

    hardRefreshPermissions() {
        this.invalidatePermissionCache();
        this._getPermissions().subscribe(() => {
            this.refreshPermissions.next();
        });
    }

    private isPermissionCacheValid(): boolean {
        return !!this.currentPermissions()
            && this.permissionCacheUserKey === this.getUserCacheKey(this.authService.currentUser.value);
    }

    private _getPermissions(): Observable<UserPermissions[]> {
        if (this.isPermissionCacheValid()) {
            return of(this.currentPermissions()!)
        }

        // Capture the user this request is for, so a late response from a
        // previous user can't be cached against a newly logged-in user.
        const requestUserKey = this.getUserCacheKey(this.authService.currentUser.value)
        if (!requestUserKey) {
            return of([]);
        }

        // User switched (login / logout) while a previous fetch was in flight.
        if (this.permissionsLoad$ && this.permissionsLoadUserKey !== requestUserKey) {
            this.clearInFlightPermissionsLoad();
        }

        if (!this.permissionsLoad$) {
            this.permissionsLoadUserKey = requestUserKey;
            this.permissionsLoad$ = this._getUserPermissionData().pipe(
                map((data) => {
                    const permissions = data.success && Array.isArray(data.data)
                        ? data.data as UserPermissions[]
                        : []
                    if (data.success && requestUserKey === this.getUserCacheKey(this.authService.currentUser.value)) {
                        this.currentPermissions.set(permissions)
                        this.permissionCacheUserKey = requestUserKey
                    }
                    return permissions
                }),
                finalize(() => this.clearInFlightPermissionsLoad()),
                shareReplay({ bufferSize: 1, refCount: false }),
            );
        }

        return this.permissionsLoad$;
    }

    _getUserPermissionData() {
        return this.get<ResponseJson<UserPermissions[]>>(this.apiUrl + '/user/')
    }

    /**
     * Whether the user's role permissions include the given type (any access
     * level). Unlike hasRead(), this does NOT bypass for SYS_ADMIN — it is used
     * for resolving layout/view selection, not for gating access.
     */
    hasPermissionType(permission: PERMISSIONS): Observable<boolean> {
        return this._getPermissions().pipe(
            map(data => data.some(permissionData => permissionData.type == permission))
        )
    }

    private _hasPermission(userPermissions: PERMISSIONS[], access: PERMISSION_ACCESS) {
        if (this.currentUser?.userType == EXEC_TYPE.SYS_ADMIN) {
            return of(true)
        }
        return this._getPermissions().pipe(
            map(data => {
                for (const permission of userPermissions) {
                    const found = data.find(permissionData => {
                        return (
                            permissionData.type == permission &&
                            this.accessLevelValueMap[access] <= this.accessLevelValueMap[permissionData.access]
                        )
                    })
                    if (found) return true;
                }
                return false
            })
        )
    }

    hasAdmin(permission: PERMISSIONS[] | PERMISSIONS) {
        permission = Array.isArray(permission) ? permission : [permission]
        return this._hasPermission(permission, PERMISSION_ACCESS.ADMIN)
    }

    hasWrite(permission: PERMISSIONS[] | PERMISSIONS) {
        permission = Array.isArray(permission) ? permission : [permission]
        return this._hasPermission(permission, PERMISSION_ACCESS.WRITE)
    }

    hasRead(permission: PERMISSIONS[] | PERMISSIONS) {
        permission = Array.isArray(permission) ? permission : [permission]
        return this._hasPermission(permission, PERMISSION_ACCESS.READ)
    }

    isDevUser() {
        return of(this.currentUser?.isDev ?? false)
    }

}
